import { HttpClient } from '@angular/common/http';
import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BlockBlobClient } from '@azure/storage-blob';
import {
  BehaviorSubject,
  catchError,
  concat,
  defer,
  finalize,
  from,
  ignoreElements,
  map,
  mergeMap,
  Observable,
  retry,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
  timer,
} from 'rxjs';

import { Nullable, replaceParams } from '@evc/web-components';

import { PlatformConfigService } from '../../../features/config/config.service';
import { STORAGE_API_ENDPOINTS } from '../../../features/config/config.type';
import type { ApiResponse } from '../../../features/types/api.type';
import {
  StorageApiUploadCompleteResponse,
  StorageApiUploadInitPayload,
  StorageApiUploadInitResponse,
  StorageUploadPayload,
  StorageUploadProgress,
  StorageUploadProgressPayload,
} from './upload.type';

export const UPLOAD_ERROR_USER_CANCELLED = 'Upload cancelled by user';
export const UPLOAD_ERROR_EMPTY_FILE = 'Cannot upload empty file';

@Injectable({
  providedIn: 'root',
})
export class UploadService {
  readonly #DEFAULT_CHUNK_SIZE = 4 * 1024 * 1024;
  readonly #DEFAULT_PARALLEL_UPLOADS = 4;
  readonly #DEFAULT_MAX_RETRIES = 3;
  readonly #DEFAULT_INITIAL_DELAY = 1000;
  readonly #DEFAULT_BACKOFF_FACTOR = 2;

  readonly #configService = inject(PlatformConfigService);
  readonly #httpClient = inject(HttpClient);
  readonly #destroyRef = inject(DestroyRef);

  progressSubject = new BehaviorSubject<Nullable<StorageUploadProgressPayload>>(null);

  /**
   * Holds cancellation subjects for currently active uploads.
   * When starting a new upload, we register its Subject here to allow cancellation.
   * Key: fileName (not uploadId, because uploadId comes async from backend), Value: Subject that triggers cancellation when emitted
   */
  #activeQueueCancellationSubjects = new Map<string, Subject<void>>();

  /**
   * Tracks files that were cancelled before uploadChunks$ started.
   * This prevents uploads from starting if they were already cancelled.
   * Key: fileName, Value: true
   */
  #preCancelledFiles = new Set<string>();

  /** Must begin with this init - you'll get file id and chunk sizes
   * then expect to chain uploadChunks$ and finally completeUpload$
   */
  initUpload$(
    payload: StorageApiUploadInitPayload,
  ): Observable<StorageApiUploadInitResponse['result']> {
    this.#debug('initUpload$', { payload });

    // Check if this file was pre-cancelled before initUpload$ started
    if (this.#preCancelledFiles.has(payload.fileName)) {
      this.#debug('initUpload$ - file was pre-cancelled, aborting', { fileName: payload.fileName });
      this.#preCancelledFiles.delete(payload.fileName);
      return throwError(() => new Error(UPLOAD_ERROR_USER_CANCELLED));
    }

    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.transfer.upload.init)
      .pipe(
        switchMap((uri) => this.#httpClient.post<StorageApiUploadInitResponse>(uri, payload)),
        map(({ result }) => result),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /** Must be called when uploaded all file chunks
   * this will complete the upload and make the file available.
   */
  completeUpload$(uploadId: string): Observable<StorageApiUploadCompleteResponse> {
    this.#debug('completeUpload$', { uploadId });

    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.transfer.upload.complete)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.post<StorageApiUploadCompleteResponse>(
            `${replaceParams(uri, { uploadId })}`,
            {},
          ),
        ),
        takeUntilDestroyed(this.#destroyRef),
        tap({
          complete: () => {
            this.#debug(`completeUpload complete`, { uploadId });
          },
        }),
      );
  }

  /** Cancels an in-progress upload on the backend.
   * @param uploadId The upload session ID returned by `initUpload$`.
   * @param fileName The original file name.
   * @param failed Whether the abort is due to a failure rather than a user cancellation.
   */
  abortUpload$(
    uploadId: string,
    fileName: string,
    failed?: boolean,
  ): Observable<ApiResponse<boolean>> {
    this.#debug('abortUpload$', { uploadId, fileName, failed });

    return this.#configService
      .safelyGetApiUrl$('STORAGE', STORAGE_API_ENDPOINTS.transfer.upload.id)
      .pipe(
        switchMap((uri) =>
          this.#httpClient.delete<ApiResponse<boolean>>(
            `${replaceParams(uri, { uploadId })}?failed=${Boolean(failed)}`,
          ),
        ),
        takeUntilDestroyed(this.#destroyRef),
      );
  }

  /**
   * use AzureStorage library to upload by chunks
   * - expect to call initUpload$ first
   * - and complete with completeUpload$ after upload is done / or abortUpload$
   *
   * Iterative method - expect many calls (one per chunk)
   * Commits chunk list after all chunks are uploaded
   */
  uploadChunks$({ sasUri, file, options }: StorageUploadPayload): Observable<number> {
    this.#debug('uploadChunks$', { sasUri, file, options });

    // Skip empty files (0 bytes)
    if (file.size === 0) {
      this.#debug('uploadChunks$ - file is empty (0 bytes), skipping', { fileName: file.name });
      return throwError(() => new Error(UPLOAD_ERROR_EMPTY_FILE));
    }

    // Check if this file was pre-cancelled before uploadChunks$ started
    if (this.#preCancelledFiles.has(file.name)) {
      this.#debug('uploadChunks$ - file was pre-cancelled, aborting', { fileName: file.name });
      this.#preCancelledFiles.delete(file.name);
      return throwError(() => new Error(UPLOAD_ERROR_USER_CANCELLED));
    }

    const { chunkSize, parallelUploads, maxRetries, retryDelay, retryBackoff } = {
      ...{
        chunkSize: this.#DEFAULT_CHUNK_SIZE,
        parallelUploads: this.#DEFAULT_PARALLEL_UPLOADS,
        maxRetries: this.#DEFAULT_MAX_RETRIES,
        retryDelay: this.#DEFAULT_INITIAL_DELAY,
        retryBackoff: this.#DEFAULT_BACKOFF_FACTOR,
      },
      ...options,
    };

    const chunksCount = Math.ceil(file.size / chunkSize);
    const chunkIndices = Array.from({ length: chunksCount }, (_, i) => i);

    const blobClient = new BlockBlobClient(sasUri);
    const cancellationSubject$ = new Subject<void>();

    // Use fileName as key for cancellation, not uploadId (uploadId comes async from backend)
    this.cancelActiveUpload(file.name);
    this.#registerCancelationSubject(file.name, cancellationSubject$);

    const totalProgress: StorageUploadProgress = {
      bytesUploaded: 0,
      percentComplete: 0,
      status: 'pending',
      blockIds: [],
    };

    totalProgress.status = 'uploading';

    this.progressSubject.next({
      fileName: file.name,
      progress: 0,
      uploaded: 0,
      size: file.size,
    });

    const uploadFileChunks$ = from(chunkIndices).pipe(
      mergeMap((chunkIndex) => {
        if (totalProgress.status === 'cancelled') {
          return throwError(() => new Error(UPLOAD_ERROR_USER_CANCELLED));
        }

        const start = chunkIndex * chunkSize;
        const end = Math.min(file.size, start + chunkSize);
        const chunk = file.slice(start, end);
        const chunkLength = end - start;

        const blockIdStr = `block-${chunkIndex.toString().padStart(6, '0')}`;
        const blockId = btoa(blockIdStr);

        totalProgress.blockIds!.push(blockId);

        return from(blobClient.stageBlock(blockId, chunk, chunkLength)).pipe(
          retry({
            count: maxRetries,
            delay: (_error, retryCount) => {
              if (totalProgress.status === 'cancelled') {
                return throwError(() => new Error(UPLOAD_ERROR_USER_CANCELLED));
              }
              const delayMs = retryDelay * retryBackoff ** (retryCount - 1);
              return timer(delayMs);
            },
          }),
          map(() => {
            this.#debug(`- chunk uploaded`, { chunkIndex, chunkLength });

            if (totalProgress.status === 'cancelled') {
              this.#debug(`chunk ${chunkIndex} completed but upload was cancelled`);
              throw new Error(UPLOAD_ERROR_USER_CANCELLED);
            }

            totalProgress.bytesUploaded += chunkLength;
            totalProgress.percentComplete = Math.round(
              (totalProgress.bytesUploaded / file.size) * 100,
            );

            this.progressSubject.next({
              fileName: file.name,
              progress: totalProgress.percentComplete,
              uploaded: totalProgress.bytesUploaded,
              size: file.size,
            });

            return totalProgress.percentComplete;
          }),
        );
      }, parallelUploads),
    );

    // Set up cancellation handler - when triggered, mark as cancelled and throw error
    cancellationSubject$.subscribe(() => {
      totalProgress.status = 'cancelled';
      this.#debug('cancellation triggered');
    });

    // We need to commit blocks that we staged
    const commitBlockList$: Observable<never> = defer(() => {
      if (totalProgress.status === 'cancelled') {
        return throwError(() => new Error(UPLOAD_ERROR_USER_CANCELLED));
      }

      return from(blobClient.commitBlockList(totalProgress.blockIds!)).pipe(
        retry({
          count: maxRetries,
          delay: (_error, retryCount) => {
            if (totalProgress.status === 'cancelled') {
              return throwError(() => new Error(UPLOAD_ERROR_USER_CANCELLED));
            }
            return timer(retryDelay * retryBackoff ** (retryCount - 1));
          },
        }),
      );
    }).pipe(ignoreElements());

    return concat(uploadFileChunks$, commitBlockList$).pipe(
      catchError((err) => {
        this.#debug(`uploadChunks error`, totalProgress, { err });

        const cancelled =
          err.message === UPLOAD_ERROR_USER_CANCELLED || totalProgress.status === 'cancelled';

        totalProgress.status = cancelled ? 'cancelled' : 'error';
        totalProgress.message = err.message ?? 'Unknown error';

        if (cancelled) {
          this.progressSubject.next({
            fileName: file.name,
            progress: -1,
            uploaded: totalProgress.bytesUploaded,
            size: file.size,
          });
        }

        return throwError(() => err);
      }),
      tap({
        complete: () => {
          totalProgress.status = 'complete';
        },
      }),
      takeUntilDestroyed(this.#destroyRef),
      takeUntil(cancellationSubject$),
      finalize(() => {
        this.#debug(`uploadChunks done`, { status: totalProgress.status, fileName: file.name });

        cancellationSubject$.complete();
        this.#activeQueueCancellationSubjects.delete(file.name);
      }),
    );
  }

  #registerCancelationSubject(key: string, subject$: Subject<void>): void {
    this.#activeQueueCancellationSubjects.set(key, subject$);
  }

  /**
   * Cancels an active upload if one exists with the given key (fileName or uploadId).
   * (or marks it as pre-cancelled if upload not started)
   *
   * Returns true if an active upload was found and cancelled
   */
  cancelActiveUpload(key: string): boolean {
    const cancellationSubject$ = this.#activeQueueCancellationSubjects.get(key);
    this.#debug(`cancelActiveUpload`, {
      key,
      hasActive: !!cancellationSubject$,
    });

    if (cancellationSubject$) {
      cancellationSubject$.next();

      return true;
    }

    this.#preCancelledFiles.add(key);
    return false;
  }

  /**
   * Clears all active upload cancellers and pre-cancelled files.
   * Call this when the upload queue is fully processed to allow re-uploading files.
   * This should be called by upload.effect after queue completion.
   */
  clearActiveQueue(): void {
    this.#debug('clearActiveQueue', {
      activeCancellations: this.#activeQueueCancellationSubjects.size,
      preCancelled: this.#preCancelledFiles.size,
    });
    this.#activeQueueCancellationSubjects.clear();
    this.#preCancelledFiles.clear();
  }

  /**
   * show logs only if DEBUG_AUTH_FLOW is set to true
   */
  #debug(msg: string, ...args: unknown[]): void {
    if (!this.#configService.getEnv('DEBUG_AUTH_FLOW')) return;

    // eslint-disable-next-line no-console
    console.debug(`[upload] ${msg}`, ...args);
  }
}
